import axios from "axios";
import Cookies from "js-cookie";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:8008"
).replace(/\/+$/, "");
const PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.GOOGLE_PLACES_API_KEY;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes timeout for long operations like answer generation
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for authentication and logging
api.interceptors.request.use(
  (config) => {
    // Add authentication token
    const authToken = Cookies.get("authToken");
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // console.log(
    //   `Making ${config.method?.toUpperCase()} request to ${config.url}`
    // );
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error("API Error:", error.response?.data || error.message);

    // Handle 401 errors by redirecting to login
    if (error.response?.status === 401) {
      // Clear auth data
      Cookies.remove("authToken");
      localStorage.removeItem("user");

      // Redirect to login if not already there
      if (window.location.pathname !== "/auth/login") {
        window.location.href = "/auth/login";
      }
    }

    // Handle 503 errors (maintenance mode)
    if (
      error.response?.status === 503 &&
      error.response?.data?.maintenance_mode
    ) {
      // Check if user is admin - if not, force page reload to show maintenance page
      const user = localStorage.getItem("user");
      let isAdmin = false;

      if (user) {
        try {
          const userData = JSON.parse(user);
          isAdmin =
            userData.active_role === "admin" ||
            (userData.roles && userData.roles.includes("admin"));
        } catch (e) {
          console.error("Error parsing user data:", e);
        }
      }

      // If not admin and not on maintenance-allowed routes, reload to trigger MaintenanceCheck
      if (
        !isAdmin &&
        !["/auth/login", "/auth/register", "/maintenance"].includes(
          window.location.pathname
        )
      ) {
        window.location.reload();
      }
    }

    return Promise.reject(error);
  }
);

// Courses API
export const coursesApi = {
  getAll: (params = {}) => api.get("/api/courses/", { params }),
  getById: (id) => api.get(`/api/courses/${id}`),
  getWithDetails: (id) => api.get(`/api/courses/${id}/details`),
  getOverviewVideoUrl: () => api.get("/api/courses/overview-video-url"),
  getQuestions: (id, params = {}) =>
    api.get(`/api/courses/${id}/questions`, { params }),
  getMyCreatedCourses: (params = {}) =>
    api.get("/api/courses/my-created-courses", { params }),
  getCreatorDashboardStats: () =>
    api.get("/api/courses/creator/dashboard/stats"),
  getMyCreatedBatches: (params = {}) =>
    api.get("/api/courses/my-created-batches", { params }),
  create: (data) => api.post("/api/courses/", data),
  update: (id, data) => api.put(`/api/courses/${id}`, data),
  delete: (id) => api.delete(`/api/courses/${id}`),
  // Progress APIs
  getProgress: (courseId) => api.get(`/api/courses/${courseId}/progress`),
  completeQuestionProgress: (courseId, questionId, enrollmentId = null) => {
    const payload = enrollmentId
      ? { enrollment_id: enrollmentId, question_id: questionId }
      : { question_id: questionId };
    return api.post(
      `/api/courses/${courseId}/progress/complete-question`,
      payload
    );
  },
  uncompleteQuestionProgress: (courseId, questionId, enrollmentId = null) => {
    const payload = enrollmentId
      ? { enrollment_id: enrollmentId, question_id: questionId }
      : { question_id: questionId };
    return api.post(
      `/api/courses/${courseId}/progress/uncomplete-question`,
      payload
    );
  },

  // Thumbnail management
  uploadThumbnail: (courseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/api/courses/${courseId}/upload-thumbnail`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  regenerateThumbnail: (courseId) =>
    api.post(`/api/courses/${courseId}/generate-thumbnail`),

  // Add question papers to existing course
  addQuestionPapers: (courseId, formData) => {
    return api.post(
      `/api/enhanced-admin/courses/${courseId}/add-questions`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  // Question Batches
  getQuestionBatches: (courseId) =>
    api.get(`/api/enhanced-admin/courses/${courseId}/question-batches`),

  pushQuestionBatchToReview: (courseId, batchId) =>
    api.post(
      `/api/enhanced-admin/courses/${courseId}/question-batches/${batchId}/push-to-review`
    ),

  deleteQuestionBatch: (courseId, batchId) =>
    api.delete(
      `/api/enhanced-admin/courses/${courseId}/question-batches/${batchId}`
    ),

  // Processing Jobs - for tracking batch question processing
  getProcessingJobStatus: (jobId) =>
    api.get(`/api/enhanced-admin/processing-jobs/${jobId}/status`),

  getCourseProcessingJobs: (courseId, limit = 10) =>
    api.get(`/api/enhanced-admin/courses/${courseId}/processing-jobs`, {
      params: { limit },
    }),
};

// Chapters API`
export const chaptersApi = {
  getByCourse: (courseId, params = {}) =>
    api.get("/api/chapters/", { params: { course_id: courseId, ...params } }),
  getById: (id) => api.get(`/api/chapters/${id}`),
  create: (data) => api.post("/api/chapters/", data),
  update: (id, data) => api.put(`/api/chapters/${id}`, data),
  delete: (id) => api.delete(`/api/chapters/${id}`),
};

// Topics API
export const topicsApi = {
  getByChapter: (chapterId, params = {}) =>
    api.get("/api/topics/", { params: { chapter_id: chapterId, ...params } }),
  getById: (id) => api.get(`/api/topics/${id}`),
  create: (data) => api.post("/api/topics/", data),
  update: (id, data) => api.put(`/api/topics/${id}`, data),
  delete: (id) => api.delete(`/api/topics/${id}`),
};

// Questions API
export const questionsApi = {
  getAll: (params = {}) => api.get("/api/questions/", { params }),
  getById: (id) => api.get(`/api/questions/${id}`),
  getByTopics: (courseId) => api.get(`/api/questions/by-topics/${courseId}`),
  // Mirrors profileApi.downloadImage: fetch the figure through the authenticated
  // backend proxy (private GCS bucket) as a blob — browser never hits GCS directly.
  downloadFigure: (courseId, filename) =>
    api.get(`/api/questions/figures/${courseId}/${filename}`, {
      responseType: "blob",
    }),
  create: (data) => api.post("/api/questions/", data),
  update: (id, data) => api.put(`/api/questions/${id}`, data),
  delete: (id) => api.delete(`/api/questions/${id}`),
  moveQuestion: (id, topicId) => api.put(`/api/questions/${id}/move`, { topic_id: topicId }),
  // Recovery: regenerate the "📚 Additional Learning Resources" section
  // for a single question whose answer was saved without links (Rufous
  // timeout, filtering, etc.). Synchronous — typically returns in seconds.
  generateMissingLinks: (questionId) =>
    api.post(`/api/enhanced-admin/questions/${questionId}/generate-missing-links`),
};

// Answers API
export const answersApi = {
  getByQuestion: (questionId, params = {}) =>
    api.get("/api/answers/", {
      params: { question_id: questionId, ...params },
    }),
  getById: (id) => api.get(`/api/answers/${id}`),
  create: (data) => api.post("/api/answers/", data),
  update: (id, data) => api.put(`/api/answers/${id}`, data),
  delete: (id) => api.delete(`/api/answers/${id}`),
};

// Admin API - Enhanced for curriculum-first workflow
// Enhanced Workflows API
export const workflowsApi = {
  // Workflow 1: Syllabus Upload
  uploadSyllabus: (formData) => {
    return api.post("/workflows/syllabus-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  getSyllabusProcessingStatus: (courseId) => {
    return api.get(`/workflows/syllabus-processing-status/${courseId}`);
  },

  // Workflow 2A: Question Extraction Only
  extractQuestions: (formData) => {
    return api.post("/workflows/question-extraction-only", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Workflow 2B: Topic Mapping Only
  mapQuestionsToTopics: (formData) => {
    return api.post("/workflows/topic-mapping-only", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Workflow: Retry Failed Mappings
  retryFailedMappings: (formData) => {
    return api.post("/workflows/retry-failed-mappings", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Workflow 2: Question Upload with Mapping (Combined)
  uploadQuestions: (formData) => {
    return api.post("/workflows/question-upload-enhanced", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  getQuestionProcessingStatus: (courseId) => {
    return api.get(`/workflows/question-processing-status/${courseId}`);
  },

  getExtractionStatus: (courseId) => {
    return api.get(`/workflows/extraction-status/${courseId}`);
  },

  getMappingStatus: (courseId) => {
    return api.get(`/workflows/mapping-status/${courseId}`);
  },

  getUnmappedQuestions: (courseId) => {
    return api.get(`/workflows/unmapped-questions/${courseId}`);
  },

  getSimilarQuestions: (questionId) => {
    return api.get(`/workflows/similar-questions/${questionId}`);
  },

  getCourseStructure: (courseId) => {
    return api.get(`/workflows/course-structure/${courseId}`);
  },

  manualTopicMapping: (formData) => {
    return api.post("/workflows/manual-topic-mapping", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Workflow 3: Answer Generation
  generateAnswers: (data) => {
    return api.post("/workflows/generate-answers", data);
  },

  getAnswerGenerationStatus: (courseId) => {
    return api.get(`/workflows/answer-generation-status/${courseId}`);
  },

  getQuestionAnswers: (questionId) => {
    return api.get(`/workflows/question-answers/${questionId}`);
  },

  getGenerationFlowStatus: (flowId) => {
    return api.get(`/workflows/generation-flow-status/${flowId}`);
  },

  // Utility methods
  getWorkflowJobs: (courseId) => {
    return api.get(`/workflows/jobs/${courseId}`);
  },

  // Progress tracking methods
  getProgressStatus: (jobId) => {
    return api.get(`/workflows/progress-status/${jobId}`);
  },

  // Note: Progress streaming is handled directly by EventSource in components
  // Stream URL: `/api/workflows/progress/${jobId}`
};

export const creatorApi = {
  // Legacy methods (for backward compatibility)
  createCourseWithFiles: (formData) => {
    return api.post("/api/admin/courses/create-with-files", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Enhanced curriculum-first workflow methods
  createCourseWithCurriculum: (formData) => {
    return api.post(
      "/api/enhanced-admin/courses/create-with-curriculum",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  getProcessingStatus: (courseId) => {
    return api.get(`/api/enhanced-admin/courses/${courseId}/processing-status`);
  },

  generateComprehensiveAnswers: (courseId, iteration = 1) => {
    const formData = new FormData();
    formData.append("iteration", iteration);
    return api.post(
      `/api/enhanced-admin/courses/${courseId}/generate-answers`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },

  getAnswersSummary: (courseId) => {
    return api.get(`/api/enhanced-admin/courses/${courseId}/answers-summary`);
  },

  mapQuestionsToTopics: (courseId) => {
    const formData = new FormData();
    formData.append("course_id", courseId);
    return api.post("/api/enhanced-admin/questions/map-to-topics", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Legacy methods
  processFile: (formData) => {
    return api.post("/api/admin/files/process", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  saveExtractedQuestions: (courseId, questionsData) => {
    const formData = new FormData();
    formData.append("course_id", courseId);
    formData.append("questions_data", JSON.stringify(questionsData));
    return api.post("/api/admin/questions/save-extracted", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  generateAnswer: (questionId, topicContext = "") => {
    return api.post(`/api/admin/questions/${questionId}/generate-answer`, {
      topic_context: topicContext,
    });
  },
  generateAllAnswers: (courseId) => {
    return api.post(`/api/admin/courses/${courseId}/generate-all-answers`);
  },
  getCourseSummary: (courseId) => {
    return api.get(`/api/admin/courses/${courseId}/questions-summary`);
  },

  getQuestionsByTopics: (courseId) => {
    return api.get(
      `/api/enhanced-admin/courses/${courseId}/questions-by-topics`
    );
  },

  // Mock Tests — 3 timed (3-hour) mock exams per student per course
  getMockTests: (courseId) =>
    api.get(`/api/enhanced-admin/courses/${courseId}/mock-tests`),

  startMockTest: (courseId, mockNumber) =>
    api.post(`/api/enhanced-admin/courses/${courseId}/mock-tests/${mockNumber}/start`),

  generateMockTestAnswers: (courseId, mockNumber) =>
    api.post(`/api/enhanced-admin/courses/${courseId}/mock-tests/${mockNumber}/generate-answers`),

  // Course workflow management
  pushCourseToReview: (courseId, data) =>
    api.post(`/api/course-editor/courses/${courseId}/push-for-review`, data),

  deleteCourse: (courseId) => {
    return api.delete(`/api/courses/${courseId}`);
  },

  updateCourseStatus: (courseId, statusData) => {
    return api.put(
      `/api/enhanced-admin/courses/${courseId}/status`,
      statusData
    );
  },

  // Add questions to existing course
  addQuestionsToCoursePage: (courseId, formData) => {
    return api.post(
      `/api/enhanced-admin/courses/${courseId}/add-questions`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  },
};

// Enrollment API
export const enrollmentApi = {
  enroll: (courseId, paymentMethod = null) =>
    api.post("/api/enrollments/enroll", {
      course_id: courseId,
      payment_method: paymentMethod,
    }),

  checkEnrollment: (courseId) => api.get(`/api/enrollments/check/${courseId}`),

  // Backend-confirmed progress for a specific course
  getProgress: (courseId) => api.get(`/api/enrollments/progress/${courseId}`),

  getMyEnrollments: (activeOnly = true, archiveStatus = "active") =>
    api.get("/api/enrollments/my-courses", {
      params: { active_only: activeOnly, archive_status: archiveStatus },
    }),

  // Archive course (matches backend: /api/courses/{course_id}/archive)
  archiveCourse: (courseId) => api.patch(`/api/courses/${courseId}/archive`),

  unarchiveCourse: (courseId) =>
    api.patch(`/api/courses/${courseId}/unarchive`),

  // updateProgress: (courseId, completionPercentage) =>
  //   api.put(`/api/enrollments/progress/${courseId}`, null, {
  //     params: { completion_percentage: completionPercentage },
  //   }),

  // Admin endpoints
  getCourseEnrollments: (courseId) =>
    api.get(`/api/enrollments/admin/course/${courseId}`),

  adminUnenroll: (userId, courseId) =>
    api.delete(`/api/enrollments/admin/unenroll/${userId}/${courseId}`),
};

// Add enrolled courses endpoint to courses API
coursesApi.getEnrolledCourses = (params = {}) =>
  api.get("/api/courses/enrolled/my-courses", { params });

// Wishlist API
export const wishlistApi = {
  add: (courseId) => api.post(`/api/wishlist/add/${courseId}`),
  remove: (courseId) => api.delete(`/api/wishlist/remove/${courseId}`),
  getWishlist: () => api.get("/api/wishlist/"),
  checkStatus: (courseId) => api.get(`/api/wishlist/check/${courseId}`),
  getCount: () => api.get("/api/wishlist/count"),
  moveToCart: (courseId) => api.post(`/api/wishlist/move-to-cart/${courseId}`),
  clear: () => api.delete("/api/wishlist/clear"),
};

// Cart API
export const cartApi = {
  // Get all items in cart
  getCartItems: () => api.get("/api/cart/"),

  // Add item to cart
  addToCart: (courseId) => api.post(`/api/cart/add/${courseId}`),

  // Remove item from cart
  removeFromCart: (courseId) => api.delete(`/api/cart/remove/${courseId}`),

  // Clear entire cart
  clearCart: () => api.delete("/api/cart/clear"),

  // Update cart item quantity (if needed)
  updateQuantity: (courseId, quantity) =>
    api.patch(`/api/cart/${courseId}`, { quantity }),
};

// Super Admin API
export const analyticsApi = {
  getCourseProgressStats: (courseId) => api.get(`/api/analytics/courses/${courseId}/progress-stats`),
};

export const superAdminApi = {
  // Dashboard & Stats
  getPlatformStats: () => api.get("/api/super-admin/dashboard/stats"),

  // Backfill regulation on appeared_in entries that are missing one
  backfillRegulation: (courseId, regulation) =>
    api.post("/api/super-admin/backfill-regulation", {
      course_id: courseId,
      regulation,
    }),

  // User Management
  getAllUsers: (params = {}) => api.get("/api/super-admin/users", { params }),
  createAdminUser: (userData) =>
    api.post("/api/super-admin/users/create", userData),
  updateUser: (userId, userData) =>
    api.put(`/api/super-admin/users/${userId}`, userData),
  assignMultipleRoles: (userId, roleData) =>
    api.put(`/api/super-admin/users/${userId}/roles`, roleData),
  performUserAction: (userId, actionData) =>
    api.post(`/api/super-admin/users/${userId}/action`, actionData),
  performBulkUserAction: (bulkActionData) =>
    api.post("/api/super-admin/users/bulk-action", bulkActionData),
  getUserDetail: (userId) =>
    api.get(`/api/super-admin/users/${userId}/detail`),
  getUserProfileImage: (userId) =>
    api.get(`/api/super-admin/users/${userId}/profile-image`, { responseType: "blob" }),
  getGrantableCourses: (userId, params = {}) =>
    api.get(`/api/super-admin/users/${userId}/grantable-courses`, { params }),
  grantCourse: (userId, courseId) =>
    api.post(`/api/super-admin/users/${userId}/grant-course`, { course_id: courseId }),
  // Permanently delete a user and all their related data (enrollments, payments,
  // progress, coupon uses, etc.). Irreversible - guard with a confirm dialog.
  deleteUser: (userId) => api.delete(`/api/super-admin/users/${userId}`),
  recalculateCourseProgress: (courseId) =>
    api.post(`/api/super-admin/courses/${courseId}/recalculate-progress`),

  // System Settings
  getPublicSettings: () => api.get("/api/super-admin/settings/public"),
  getSystemSettings: () => api.get("/api/super-admin/settings"),
  updateSystemSettings: (settingsData) =>
    api.put("/api/super-admin/settings", settingsData),
  toggleMaintenanceMode: (enabled) =>
    api.post("/api/super-admin/maintenance", { enabled }),

  // Review / approval deadline reminders
  runRemindersNow: () => api.post("/api/super-admin/reminders/run"),

  // Workflow Management
  getCourseWorkflows: (params = {}) =>
    api.get("/api/super-admin/course-status", { params }),
  getAvailableReviewers: () => api.get("/api/super-admin/reviewers"),
  assignReviewer: (courseId, assignmentData) =>
    api.post(
      `/api/super-admin/courses/${courseId}/assign-reviewer`,
      assignmentData
    ),
  reassignReviewer: (courseId, assignmentData) =>
    api.put(
      `/api/super-admin/courses/${courseId}/reassign-reviewer`,
      assignmentData
    ),
  getAvailableApprovers: () => api.get("/api/super-admin/approvers"),
  assignApprover: (courseId, assignmentData) =>
    api.post(
      `/api/super-admin/courses/${courseId}/assign-approver`,
      assignmentData
    ),
  reassignApprover: (courseId, assignmentData) =>
    api.put(
      `/api/super-admin/courses/${courseId}/reassign-approver`,
      assignmentData
    ),
  manageWorkflow: (courseId, workflowData) =>
    api.post(
      `/api/super-admin/courses/${courseId}/workflow-action`,
      workflowData
    ),
  takeDownCourse: (courseId, comments) =>
    api.post(`/api/super-admin/courses/${courseId}/workflow-action`, {
      action: "take_down",
      comments,
    }),
  deleteCourse: (courseId) =>
    api.delete(`/api/super-admin/courses/${courseId}`),

  // Pricing Management
  updateCoursePricing: (courseId, pricingData) =>
    api.put(`/api/super-admin/courses/${courseId}/pricing`, pricingData),

  // Audit & Logs
  getAdminLogs: (params = {}) => api.get("/api/super-admin/logs", { params }),

  // Coupon Management
  generateCoupon: (couponData) =>
    api.post("/api/super-admin/coupon/generate", couponData),
  getAllCoupons: (params = {}) => api.get("/api/super-admin/coupons", { params }),
  getCouponUsage: (couponId) => api.get(`/api/super-admin/coupons/${couponId}/usage`),
  updateCoupon: (couponId, data) => api.put(`/api/super-admin/coupons/${couponId}`, data),
  toggleCoupon: (couponId) => api.post(`/api/super-admin/coupons/${couponId}/toggle`),
  validateCoupon: (data) => api.post("/api/user/validate-coupon", data),
  applyCoupon: (data) => api.post("/api/user/apply-coupon", data),
  getActiveCoupons: () => api.get("/api/user/coupons/available"),

  // Question Batch Management
  getQuestionBatchWorkflows: (params = {}) =>
    api.get("/api/super-admin/question-batches", { params }),
  getCourseQuestionBatches: (courseId) =>
    api.get(`/api/super-admin/courses/${courseId}/question-batches`),
  assignReviewerToQuestionBatch: (batchId, data) =>
    api.post(`/api/super-admin/question-batches/${batchId}/assign-reviewer`, data),
  assignApproverToQuestionBatch: (batchId, data) =>
    api.post(`/api/super-admin/question-batches/${batchId}/assign-approver`, data),
  reassignReviewerToQuestionBatch: (batchId, assignmentData) =>
    api.put(`/api/super-admin/question-batches/${batchId}/reassign-reviewer`, assignmentData),
  batchGoLive: (batchId) =>
    api.post(`/api/super-admin/question-batches/${batchId}/go-live`),
  batchTakeDown: (batchId) =>
    api.post(`/api/super-admin/question-batches/${batchId}/take-down`),
  deleteBatch: (batchId) =>
    api.delete(`/api/super-admin/question-batches/${batchId}`),
};

// Universities API
export const universitiesApi = {
  // List and search universities
  getAll: (params = {}) => api.get("/api/universities/", { params }),
  getActive: () => api.get("/api/universities/active"),

  // CRUD operations
  getById: (id) => api.get(`/api/universities/${id}`),
  getDetail: (id) => api.get(`/api/universities/${id}/detail`),
  create: (data) => api.post("/api/universities/", data),
  update: (id, data) => api.put(`/api/universities/${id}`, data),
  delete: (id) => api.delete(`/api/universities/${id}`),
  archive: (id) => api.patch(`/api/universities/${id}/archive`),

  // Bulk operations
  bulkImport: (universities) =>
    api.post("/api/universities/bulk-import", universities),

  // Statistics
  getStats: () => api.get("/api/universities/stats/summary"),

  // Fetch details from website
  fetchDetailsFromWebsite: (websiteUrl) =>
    api.post("/api/universities/fetch-details", { website_url: websiteUrl }),
};

// Reviewer API
export const reviewerApi = {
  // Dashboard & Stats
  getReviewerStats: () => api.get("/api/reviewer/dashboard/stats"),

  // Course Review Management
  getCoursesForReview: (params = {}) =>
    api.get("/api/reviewer/courses", { params }),
  getCoursesWithChangesRequested: () =>
    api.get("/api/reviewer/courses/changes-requested"),
  getRejectedCourses: () => api.get("/api/reviewer/courses/rejected"),
  getCourseDetails: (courseId, batchId = null) => {
    console.log("📡 API call getCourseDetails:", { courseId, batchId });
    const params = batchId ? { batch_id: batchId } : {};
    console.log("📡 Request params:", params);
    return api.get(`/api/reviewer/courses/${courseId}/details`, { params });
  },
  submitReview: (courseId, reviewData, batchId = null) => {
    const params = batchId ? { batch_id: batchId } : {};
    console.log("📝 Submitting review with batch_id:", batchId);
    return api.post(`/api/reviewer/courses/${courseId}/review`, reviewData, { params });
  },

  // Question-Level Review Management
  getQuestionsForReview: (courseId, statusFilter = null, extraParams = {}) =>
    api.get(`/api/reviewer/courses/${courseId}/questions`, {
      params: { status_filter: statusFilter, ...extraParams },
    }),
  getReviewProgress: (courseId, batchId = null) => {
    const params = batchId ? { batch_id: batchId } : {};
    return api.get(`/api/reviewer/courses/${courseId}/review-progress`, { params });
  },
  submitQuestionReview: (questionId, reviewData) =>
    api.post(`/api/reviewer/questions/${questionId}/review`, reviewData),
  updateQuestionReview: (questionId, reviewData) =>
    api.put(`/api/reviewer/questions/${questionId}/review`, reviewData),
  getQuestionReviewHistory: (questionId) =>
    api.get(`/api/reviewer/questions/${questionId}/review-history`),
};

// Approver API
export const approverApi = {
  // Dashboard & Stats
  getApproverStats: () => api.get("/api/approver/dashboard/stats"),

  // Course Approval Management
  getCoursesForApproval: (params = {}) =>
    api.get("/api/approver/courses", { params }),
  getCourseDetails: (courseId, batchId = null) => {
    const params = batchId ? { batch_id: batchId } : {};
    return api.get(`/api/approver/courses/${courseId}/details`, { params });
  },
  approveCourse: (courseId, approvalData, batchId = null) => {
    const params = batchId ? { batch_id: batchId } : {};
    return api.post(`/api/approver/courses/${courseId}/approve`, approvalData, { params });
  },
  makeCourseLive: (courseId, goLiveData) =>
    api.post(`/api/approver/courses/${courseId}/go-live`, goLiveData),
  makeQuestionBatchLive: (batchId, goLiveData) =>
    api.post(`/api/approver/question-batches/${batchId}/go-live`, goLiveData),

  // Pricing Management
  updateCoursePricing: (courseId, pricingData) =>
    api.put(`/api/approver/courses/${courseId}/pricing`, pricingData),
};

// Deadline notifications (reviewer / approver bell)
export const notificationsApi = {
  // `role` scopes to one stage ("reviewer" / "approver") for accounts that hold both.
  getDeadlines: (role) =>
    api.get("/api/notifications/deadlines", { params: role ? { role } : {} }),
};

// Question Reports API
export const questionReportsApi = {
  // Student: get my reported question IDs
  getMyReports: () => api.get("/api/question-reports/my-reports"),

  // Student: report a question
  reportQuestion: (questionId, comment) =>
    api.post(`/api/question-reports/${questionId}`, { comment }),

  // Reviewer: list reported questions
  getReviewerReports: (statusFilter = null) =>
    api.get("/api/question-reports/reviewer/list", {
      params: statusFilter ? { status_filter: statusFilter } : {},
    }),

  // Reviewer: get report detail
  getReviewerReportDetail: (reportId) =>
    api.get(`/api/question-reports/reviewer/${reportId}`),

  // Reviewer: request changes from creator
  requestChanges: (reportId, reviewerNote) =>
    api.post(`/api/question-reports/reviewer/${reportId}/request-changes`, {
      reviewer_note: reviewerNote,
    }),

  // Reviewer: dismiss invalid report
  dismissReport: (reportId) =>
    api.post(`/api/question-reports/reviewer/${reportId}/dismiss`),
};

// Course Editor API
export const courseEditorApi = {
  checkEditable: (courseId) =>
    api.get(`/api/course-editor/courses/${courseId}/editable`),
  getCourseContent: (courseId) =>
    api.get(`/api/course-editor/courses/${courseId}/content`),
  // Question text. Same endpoint the changes-requested editor saves through -
  // the backend allows it in any workflow state, but only for the creator who
  // owns the course.
  updateQuestion: (questionId, questionText) =>
    api.put(`/api/course-editor/questions/${questionId}`, {
      question_text: questionText,
    }),
  updateAnswer: (answerId, formData) =>
    api.put(`/api/course-editor/answers/${answerId}`, formData),
  uploadImage: (answerId, formData) =>
    api.post(`/api/course-editor/answers/${answerId}/images`, formData),
  deleteImage: (answerId, imageIndex) =>
    api.delete(`/api/course-editor/answers/${answerId}/images/${imageIndex}`),
  // Question figures (the original question images from the source paper)
  uploadQuestionFigure: (questionId, formData) =>
    api.post(`/api/course-editor/questions/${questionId}/figures`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteQuestionFigure: (questionId, figureIndex) =>
    api.delete(`/api/course-editor/questions/${questionId}/figures/${figureIndex}`),
  // Question table (HTML) / math (LaTeX) blocks. field is 'tables' | 'question_latex'.
  addQuestionBlock: (questionId, field, content) =>
    api.post(`/api/course-editor/questions/${questionId}/${field}`, { content }),
  updateQuestionBlock: (questionId, field, blockIndex, content) =>
    api.put(
      `/api/course-editor/questions/${questionId}/${field}/${blockIndex}`,
      { content }
    ),
  deleteQuestionBlock: (questionId, field, blockIndex) =>
    api.delete(
      `/api/course-editor/questions/${questionId}/${field}/${blockIndex}`
    ),
  resubmitCourse: (courseId) =>
    api.post(`/api/course-editor/courses/${courseId}/resubmit`),
  pushBatchToReview: (batchId, data) =>
    api.post(`/api/course-editor/question-batches/${batchId}/push-for-review`, data),

  // Question-level feedback (creator view)
  // batchId scopes the feedback to one question batch; without it the feedback
  // is the course's own questions.
  getQuestionFeedback: (courseId, statusFilter = null, batchId = null) =>
    api.get(`/api/course-editor/courses/${courseId}/question-feedback`, {
      params: { status_filter: statusFilter, ...(batchId ? { batch_id: batchId } : {}) },
    }),
  getFeedbackSummary: (courseId) =>
    api.get(`/api/course-editor/courses/${courseId}/feedback-summary`),
};

// Authentication API
export const authApi = {
  switchRole: (roleData) => api.post("/api/auth/switch-role", roleData),
  getCurrentUser: () => api.get("/api/auth/me"),
  validateToken: () => api.get("/api/auth/validate-token"),
};

// Backward compatibility
export const adminApi = creatorApi;

export default api;

// ---------------- Google Places (HTTP) ----------------
// Separate axios client for Google Places API v1
const placesClient = axios.create({
  baseURL: "https://places.googleapis.com/v1",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // We'll append the key directly in each endpoint URL for clarity
});

// Interceptors (optional logging)
placesClient.interceptors.request.use((config) => {
  console.log(
    `Google Places ${config.method?.toUpperCase()} ${config.baseURL}${config.url
    }`
  );
  return config;
});

export const googlePlacesApi = {
  // Autocomplete suggestions for a free-text input
  autocomplete: (input) => {
    if (!PLACES_API_KEY) {
      return Promise.reject(
        new Error(
          "Google Places API key is missing. Set REACT_APP_GOOGLE_PLACES_API_KEY in frontend/.env and restart the dev server."
        )
      );
    }
    return placesClient.post(
      `/places:autocomplete?fields=*&alt=json&key=${encodeURIComponent(
        PLACES_API_KEY
      )}`,
      { input }
    );
  },

  // Place details by placeId; default to full field mask
  getPlaceDetails: (placeId, fieldMask = "*") => {
    if (!PLACES_API_KEY) {
      return Promise.reject(
        new Error(
          "Google Places API key is missing. Set REACT_APP_GOOGLE_PLACES_API_KEY in frontend/.env and restart the dev server."
        )
      );
    }
    return placesClient.get(
      `/places/${encodeURIComponent(placeId)}?key=${encodeURIComponent(
        PLACES_API_KEY
      )}`,
      {
        headers: { "X-Goog-FieldMask": fieldMask },
      }
    );
  },
};

// For Adding and Updating user profile
export const profileApi = {
  uploadImage: (formData) => {
    return api.post("/api/profile/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  updateProfileImage: (payload) => { // payload: { profileImageName: "..." }
    return api.post("/api/profile/profile-image", payload);
  },
  getProfileImageMeta: () => {
    return api.get("/api/profile/profile-image");
  },
  downloadImage: (filename) => {
    return api.get(`/api/profile/download/${filename}`, {
      responseType: "blob",
    });
  },
  // Update name / mobile
  updateProfile: (data) => api.put("/api/profile/me", data),
  // Change email (OTP verified)
  requestEmailChangeOtp: (new_email) =>
    api.post("/api/profile/email/request-otp", { new_email }),
  verifyEmailChangeOtp: (new_email, otp) =>
    api.post("/api/profile/email/verify-otp", { new_email, otp }),
  // Deactivate (reversible) or delete (soft-delete) the current user's own account
  deactivateAccount: () => api.post("/api/profile/deactivate"),
  deleteAccount: () => api.delete("/api/profile/me"),
};

